import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { ApiService } from "./api.service";
import {
  ProductionRequest,
  ProductionRequestDetail,
  ProductionRequestMessage,
  ProductionRequestAudioVersion,
  Notification,
} from "../models/production-request.model";

const BASE = `${environment.apiUrl}/production-requests`;
const NOTIFICATIONS_BASE = `${environment.apiUrl}/notifications`;

@Injectable({ providedIn: "root" })
export class ProductionRequestService {
  constructor(private api: ApiService) { }

  list(params?: {
    owner_id?: number
    station_id?: number
    limit?: number
    offset?: number
  }): Observable<ProductionRequest[]> {
    const query = new URLSearchParams()
    if (params?.owner_id != null) query.set("owner_id", String(params.owner_id))
    if (params?.station_id != null) query.set("station_id", String(params.station_id))
    if (params?.limit != null) query.set("limit", String(params.limit))
    if (params?.offset != null) query.set("offset", String(params.offset))
    const qs = query.toString()
    const url = qs ? `${BASE}?${qs}` : BASE
    return this.api.authenticatedGet<ProductionRequest[]>(url)
  }

  getById(id: number): Observable<ProductionRequestDetail> {
    return this.api.authenticatedGet<ProductionRequestDetail>(`${BASE}/${id}`);
  }

  create(payload: {
    station_id: number;
    what_business_does: string;
    campaign_goal: string;
    unique_selling_point: string;
    call_to_action?: string;
    duration?: number;
  }): Observable<{ status: string; id: number; message: string }> {
    return this.api.authenticatedPost(BASE, payload);
  }

  postMessage(requestId: number, message: string): Observable<{ status: string; message: ProductionRequestMessage }> {
    return this.api.authenticatedPost(`${BASE}/${requestId}/messages`, { message });
  }

  getUploadUrl(requestId: number, body?: { extension?: string }): Observable<{
    upload_url: string;
    tmp_key: string;
    version: number;
    cloudfront_preview_url?: string;
    content_type?: string;
  }> {
    return this.api.authenticatedPost(`${BASE}/${requestId}/upload-url`, body || {});
  }

  registerAudio(
    requestId: number,
    tmpKey: string,
    audioLength?: number,
    productTypeIds: number[] = [],
    talentId?: number,
  ): Observable<{ status: string; version: number }> {
    const body: any = { tmp_key: tmpKey, product_type_ids: productTypeIds };
    if (audioLength !== undefined && audioLength > 0) {
      body.audio_length = audioLength;
    }
    if (talentId !== undefined && talentId > 0) {
      body.talentid = talentId;
    }
    return this.api.authenticatedPost(`${BASE}/${requestId}/audio`, body);
  }

  askMoreInfo(requestId: number, message: string): Observable<{ status: string }> {
    return this.api.authenticatedPost(`${BASE}/${requestId}/ask-more-info`, { message });
  }

  replyMoreInfo(requestId: number, moreInfoId: number, replyText: string): Observable<{ status: string }> {
    return this.api.authenticatedPost(`${BASE}/${requestId}/more-info-reply`, {
      more_info_id: moreInfoId,
      reply_text: replyText,
    });
  }

  approve(
    requestId: number,
    payload: {
      recording_name?: string
      station_id?: number
      audio_id?: number
      version?: number
      product_type_ids?: number[]
    }
  ): Observable<{ status: string; recording_id?: number }> {
    return this.api.authenticatedPost(`${BASE}/${requestId}/approve`, payload);
  }

  decline(
    requestId: number,
    payload: { explanation: string; missing_information?: string; audio_id?: number; version?: number }
  ): Observable<{ status: string }> {
    return this.api.authenticatedPost(`${BASE}/${requestId}/decline`, payload);
  }
}

@Injectable({ providedIn: "root" })
export class NotificationService {
  constructor(private api: ApiService) { }

  list(limit = 20): Observable<Notification[]> {
    return this.api.authenticatedGet<Notification[]>(`${NOTIFICATIONS_BASE}?limit=${limit}`);
  }

  unreadCount(): Observable<{ count: number }> {
    return this.api.authenticatedGet<{ count: number }>(`${NOTIFICATIONS_BASE}/unread-count`);
  }

  markRead(id: number): Observable<{ status: string }> {
    return this.api.authenticatedPost(`${NOTIFICATIONS_BASE}/${id}/read`, {});
  }

  /** Mark multiple notifications as read (e.g. visible in panel). */
  markReadBulk(ids: number[]): Observable<{ status: string; marked?: number }> {
    return this.api.authenticatedPost(`${NOTIFICATIONS_BASE}/read-bulk`, { ids });
  }

  /** Mark all notifications for a production request as read (e.g. when opening PR view). */
  markReadByReference(referenceId: number): Observable<{ status: string; marked?: number }> {
    return this.api.authenticatedPost(`${NOTIFICATIONS_BASE}/read-by-reference`, { reference_id: referenceId });
  }
}
